import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { PaymentMethodIframe } from '@zillow/metroid-components';

import { FrontendEnvironmentConfig } from '@zg-rentals/frontend-config';
import { logError } from '@zg-rentals/log-error';

import type { FreemiumStartPaymentMethodSessionResponseWebDto } from '@zg-rentals/ts-rental-manager-api-beta-client';

export type MetroidCaptureError = {
  code: string | null;
  code_details?: string | null;
};

export type Props = {
  sessionData?: FreemiumStartPaymentMethodSessionResponseWebDto;
  onRenderStart: () => void;
  onRenderComplete: () => void;
  onSubmitSuccess: () => void;
  onSubmitFailure: (error: MetroidCaptureError) => void;
};

const AlicornPaymentIframe = ({
  sessionData,
  onRenderStart,
  onRenderComplete,
  onSubmitSuccess,
  onSubmitFailure,
}: Props) => {
  const router = useRouter();

  const metroidAlicornUrl = new FrontendEnvironmentConfig(
    'https://metroid-billing-api-gateway-client.stage.kong-nonprod.zg-int.net/api/alicorn/client',
  )
    .withProdValue('https://metroid-billing-api-gateway.prod.zgk-partner.zillowapi.com/api/alicorn/client')
    .getValue();

  const metroidAlicornHppConfigId = new FrontendEnvironmentConfig('CC:Zillow:Rentals Professionals:localhost')
    .withStagingValue('CC:Zillow:Rentals Professionals:qa')
    .withProdValue('CC:Zillow:Rentals Professionals')
    .getValue();

  const listingAlias = router.query.listingAlias as string;

  useEffect(() => {
    onRenderStart();
  }, [onRenderStart]);

  const handleSubmitFailure = (error: MetroidCaptureError) => {
    const isMaxAttemptsError = error?.code_details?.includes('Attempt_Exceed_Limitation');
    logError({
      error,
      context: {
        message: isMaxAttemptsError ? 'Exceded HPP Attempts' : 'Could not load credit card iframe.',
        details: 'AlicornPaymentIframe.onSubmitFailure',
        listingId: listingAlias,
      },
    });
    onSubmitFailure(error);
  };

  if (!sessionData?.metroidAuthToken) {
    return null;
  }

  const metroidAccountId = {
    brand: 'Zillow',
    businessUnit: sessionData.businessUnit,
    userId: sessionData.zuoraUserToken,
  };

  return (
    <PaymentMethodIframe
      elementId="alicorn-iframe"
      onSubmitSuccess={onSubmitSuccess}
      onSubmitFailure={handleSubmitFailure}
      onRenderComplete={onRenderComplete}
      metroidAuthToken={sessionData.metroidAuthToken}
      metroidHPPConfigId={metroidAlicornHppConfigId ?? ''}
      metroidAccountId={metroidAccountId}
      metroidEndpointUrl={metroidAlicornUrl ?? ''}
      setAsDefault={true}
    />
  );
};

export default AlicornPaymentIframe;
